import { type ComponentProps } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button-next/new';
import AddIcon from '@atlaskit/icon/core/add';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import PaintBucketIcon from '@atlaskit/icon/core/paint-bucket';
import SearchIcon from '@atlaskit/icon/core/search';
import { JiraServiceManagementIcon, RovoIcon } from '@atlaskit/logo';
import { Text } from '@atlaskit/primitives-next';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { type TeamNavigationConfig } from './jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from './jsm-portal-playground-messages';

type JsmPlaygroundNavigationProps = {
	config: TeamNavigationConfig;
	teamName: string;
};

const noop = () => {};

type ButtonIcon = NonNullable<ComponentProps<typeof Button>['iconBefore']>;
type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const BackButtonIcon: IconButtonIcon = () => <ArrowLeftIcon label="" size="medium" />;
const CreateButtonIcon: ButtonIcon = () => <AddIcon label="" size="medium" />;
const CustomizeButtonIcon: ButtonIcon = () => <PaintBucketIcon label="" size="medium" />;
const AskRovoIcon: ButtonIcon = () => (
	<RovoIcon appearance="brand" label="" shouldUseHexLogo size="xxsmall" />
);

/**
 * A non-interactive representation of JSM navigation for the playground preview.
 * ADS controls sit inside an inert, presentational tree so their visuals match the product without
 * exposing actions that do nothing to keyboard or assistive-technology users.
 */
export const JsmPlaygroundNavigation = ({ config, teamName }: JsmPlaygroundNavigationProps) => {
	const intl = useIntl();

	return (
		<div
			role="img"
			aria-label={intl.formatMessage(messages.navigationPreviewLabel, { teamName })}
			css={navigationStyles}
		>
			<div
				aria-hidden="true"
				data-testid="jsm-navigation-preview-contents"
				// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
				inert=""
				css={navigationContentsStyles}
			>
				<div css={navigationStartStyles}>
					{config.showBack && (
						<IconButton
							appearance="subtle"
							icon={BackButtonIcon}
							label={intl.formatMessage(messages.back)}
							onClick={noop}
							spacing="compact"
							tabIndex={-1}
						/>
					)}

					<div css={productLockupStyles}>
						<JiraServiceManagementIcon
							appearance="brand"
							label=""
							shouldUseNewLogoDesign
							size="small"
						/>
						<Text color="color.text" maxLines={1} weight="bold">
							{intl.formatMessage(messages.productName)}
						</Text>
					</div>
				</div>

				{config.showSearch && (
					<div css={navigationMiddleStyles}>
						<div css={searchFieldWrapperStyles}>
							<Textfield
								aria-label={intl.formatMessage(messages.search)}
								appearance="none"
								elemBeforeInput={
									<span css={searchIconWrapperStyles}>
										<SearchIcon color={token('color.icon.subtle')} label="" size="small" />
									</span>
								}
								isCompact
								isReadOnly
								placeholder={intl.formatMessage(messages.search)}
								tabIndex={-1}
							/>
						</div>

						{config.showCreate && (
							<Button iconBefore={CreateButtonIcon} onClick={noop} tabIndex={-1}>
								{intl.formatMessage(messages.create)}
							</Button>
						)}
					</div>
				)}

				<div css={navigationEndStyles}>
					{config.showCustomizeHelpCenter && (
						<Button iconBefore={CustomizeButtonIcon} onClick={noop} tabIndex={-1}>
							{intl.formatMessage(messages.customizeHelpCenter)}
						</Button>
					)}

					{config.showAskRovo && (
						<Button iconBefore={AskRovoIcon} onClick={noop} tabIndex={-1}>
							{intl.formatMessage(messages.askRovo)}
						</Button>
					)}

					<span
						css={[avatarStyles, config.avatarAppearance === 'outlined' && outlinedAvatarStyles]}
					>
						<Text
							color="color.text.inverse"
							size={config.avatarAppearance === 'outlined' ? 'large' : 'medium'}
							weight="semibold"
						>
							{config.avatarInitial}
						</Text>
					</span>
				</div>
			</div>
		</div>
	);
};

const navigationStyles = css({
	'--ds-border-radius-100': token('radius.medium', '6px'),
	boxSizing: 'border-box',
	width: '100%',
	height: token('space.600'),
	flexShrink: 0,
	borderBlockEndWidth: token('border.width', '1px'),
	borderBlockEndStyle: 'solid',
	borderBlockEndColor: token('color.border'),
	backgroundColor: token('elevation.surface'),
	font: token('font.body'),
	pointerEvents: 'none',
	userSelect: 'none',
});

const navigationContentsStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	boxSizing: 'border-box',
	width: '100%',
	height: '100%',
	paddingBlock: token('space.100'),
	paddingInline: token('space.150'),
});

const navigationStartStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	minWidth: 0,
	flexShrink: 0,
});

const productLockupStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
	minWidth: 0,
	paddingInline: token('space.050'),
	whiteSpace: 'nowrap',
});

const navigationMiddleStyles = css({
	display: 'flex',
	flex: '1 1 auto',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
	maxWidth: '610px',
	marginInline: 'auto',
});

const searchFieldWrapperStyles = css({
	display: 'flex',
	flex: '1 1 auto',
	boxSizing: 'border-box',
	minWidth: 0,
	paddingBlockStart: token('border.width', '1px'),
	paddingBlockEnd: token('border.width', '1px'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border.input'),
	borderRadius: token('radius.medium', '6px'),
	backgroundColor: token('color.background.input'),
	overflow: 'hidden',
});

const searchIconWrapperStyles = css({
	display: 'inline-flex',
	paddingInlineStart: token('space.075'),
});

const navigationEndStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	gap: token('space.050'),
	flexShrink: 0,
	marginInlineStart: 'auto',
});

const avatarStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	width: token('space.300'),
	height: token('space.300'),
	flexShrink: 0,
	borderRadius: token('radius.full', '9999px'),
	backgroundColor: token('color.background.accent.blue.bolder'),
});

const outlinedAvatarStyles = css({
	width: `calc(${token('space.300')} + ${token('space.050')})`,
	height: `calc(${token('space.300')} + ${token('space.050')})`,
	border: `${token('border.width.selected', '2px')} solid ${token('elevation.surface.overlay')}`,
});
